import { useEffect, useRef } from 'react';
import { useWebSocket } from './useWebSocket';
import { useAuth } from '@/features/auth/hooks/useAuth';

/**
 * Global hook that joins the user to their personal notification room
 * when authenticated. This allows them to receive bidStatusChanged events
 * anywhere in the app (not just on auction pages).
 *
 * This is separate from auction-specific WebSocket connections - the user
 * room persists across the entire session while authenticated.
 */
export const useUserNotifications = () => {
    const { user } = useAuth();
    const { emit, isConnected, on } = useWebSocket({ namespace: '/user' });
    const hasJoinedRef = useRef(false);

    useEffect(() => {
        if (!user?.id || !isConnected || hasJoinedRef.current) return;

        emit('joinUserRoom', { userId: user.id });
        hasJoinedRef.current = true;

        return () => {
            if (hasJoinedRef.current) {
                hasJoinedRef.current = false;
                emit('leaveUserRoom', { userId: user.id });
            }
        };
    }, [user?.id, isConnected, emit]);

    // Return the on function so components can subscribe to events
    return { on };
};
